import { demoSharedApiUrl, demoSharedTenant, isSharedDemoMode } from '../config/environment';

const DEFAULT_FETCH_TIMEOUT_MS = 2500;
const DEFAULT_SAVE_TIMEOUT_MS = 5000;

const getStateUrl = () => {
    const baseUrl = demoSharedApiUrl.replace(/\/$/, '');
    const query = new URLSearchParams({ tenant: demoSharedTenant });
    return `${baseUrl}/api/state?${query.toString()}`;
};

const fetchWithTimeout = async (url, options, timeoutMs) => {
    if (typeof AbortController === 'undefined') {
        return fetch(url, options);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error(`Shared demo state request timed out after ${timeoutMs}ms`);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }
};

export const fetchSharedDemoState = async ({ timeoutMs = DEFAULT_FETCH_TIMEOUT_MS } = {}) => {
    if (!isSharedDemoMode) return null;

    const response = await fetchWithTimeout(getStateUrl(), {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
        },
    }, timeoutMs);

    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Shared demo state fetch failed: ${response.status}`);

    const payload = await response.json();
    return payload.state || payload;
};

export const saveSharedDemoState = async (state, { timeoutMs = DEFAULT_SAVE_TIMEOUT_MS } = {}) => {
    if (!isSharedDemoMode) return null;

    const response = await fetchWithTimeout(getStateUrl(), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
            tenant: demoSharedTenant,
            state,
        }),
    }, timeoutMs);

    if (!response.ok) throw new Error(`Shared demo state save failed: ${response.status}`);
    return response.json();
};
